import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X,
  CheckCircle2,
  AlertCircle,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

interface ApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  type: 'hr' | 'it';
  onSuccess: () => void;
}

export default function ApprovalModal({ isOpen, onClose, formId, type, onSuccess }: ApprovalModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [signature, setSignature] = useState('');
  const [comments, setComments] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: async () => {
      const endpoint = type === 'hr' 
        ? `/access-forms/${formId}/approve-hr`
        : `/access-forms/${formId}/approve-it`;
      
      const payload = {
        signature,
        comments,
        approverName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
        approverId: user?.id,
      };

      const response = await api.post(endpoint, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForm', formId] });
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to approve access form');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!signature.trim()) {
      setSubmitError('Please provide your signature');
      return;
    }

    approveMutation.mutate();
  };

  const resetForm = () => {
    setSignature('');
    setComments('');
    setSubmitError(null);
  };

  if (!isOpen) return null;

  const title = type === 'hr' ? 'HR Approval' : 'IT Approval';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2 text-green-600">
            <Shield className="w-5 h-5" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Error Alert */}
          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="text-destructive">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <p className="font-medium">Approve Access Request</p>
              </div>
              <p className="text-sm text-green-600 mt-1">
                You are about to approve this access request. Please provide your signature to confirm.
              </p>
            </div>

            {/* Signature */}
            <div className="space-y-2">
              <Label htmlFor="signature">
                Digital Signature <span className="text-destructive">*</span>
              </Label>
              <Input
                id="signature"
                placeholder="Type your full name as signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Your typed name will serve as your digital signature
              </p>
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <Label htmlFor="comments">Comments (Optional)</Label>
              <textarea
                id="comments"
                className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Any additional comments or notes..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={approveMutation.isPending}
            loading={approveMutation.isPending}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
}
